"use client";

/**
 * HeroVariantBeacon — fires `hero_variant_viewed` once per session.
 *
 * Lives outside the server-rendered hero. Renders nothing.
 *
 * Spec: docs/03-funnel/analytics-events.md (hero_variant_viewed row)
 *       docs/04-technical/homepage-variants-design.md §6
 *
 * Implementation choices:
 *   - sessionStorage debounce keyed by variant id, so a back-button reload
 *     during the same browsing session does not double-count.
 *   - Safari ITP / private-mode environments where sessionStorage throws
 *     are caught silently; we accept the rare double-count over a noisy
 *     console.
 *   - Source is the utm_source query param if present, else "direct".
 *     This is a client-side best-effort; the page-load source attribution
 *     is also captured server-side by `landing_viewed` if needed.
 */

import { useEffect } from "react";
import { track } from "@/lib/analytics";
import type { Angle } from "@/lib/content/homepage-variants";

interface Props {
  variantId: string;
  angle: Angle;
  isCanonical: boolean;
}

const SESSION_KEY_PREFIX = "mi:hvv:";

function readSource(): string {
  if (typeof window === "undefined") return "unknown";
  try {
    const url = new URL(window.location.href);
    const utm = url.searchParams.get("utm_source");
    if (utm && utm.trim() !== "") return utm.trim().toLowerCase();
    if (document.referrer === "") return "direct";
    try {
      const ref = new URL(document.referrer);
      if (ref.host === url.host) return "internal";
      return "referral";
    } catch {
      return "referral";
    }
  } catch {
    return "unknown";
  }
}

export function HeroVariantBeacon({ variantId, angle, isCanonical }: Props) {
  useEffect(() => {
    const sessionKey = `${SESSION_KEY_PREFIX}${variantId}`;
    let alreadyFired = false;
    try {
      alreadyFired = window.sessionStorage.getItem(sessionKey) === "1";
    } catch {
      // sessionStorage unavailable (Safari ITP, embedded iframe, etc.).
      // Treat as not fired; we accept rare double-counts.
      alreadyFired = false;
    }
    if (alreadyFired) return;

    track("hero_variant_viewed", {
      variant_id: variantId,
      angle,
      source: readSource(),
      is_canonical: isCanonical,
    });

    try {
      window.sessionStorage.setItem(sessionKey, "1");
    } catch {
      // ignored
    }
  }, [variantId, angle, isCanonical]);

  return null;
}
